import { useQuery } from '@apollo/client';
import {
  Box,
  Card,
  Divider,
  Heading,
  Stack,
  Text,
  Page,
  PageBlock,
  Hidden,
  Alert,
  TextLink,
} from 'braid-design-system';
import { useParams } from 'react-router-dom';

import CustomLoader from 'src/components/CustomLoader/CustomLoader';
import Header from 'src/components/Header/Header';
import { SiteWideFooter } from 'src/components/SiteWideFooter';
import { GetPrivateSellerEnquiriesDocument } from 'src/generated/graphql';
import { useEffectOnce } from 'src/hooks/useEffectOnce';
import { isNonNullable } from 'src/utils';
import { fireViewEnquiriesEvent } from 'src/utils/analytics';

import { Error } from '../../components/Error/Error';

import Enquiry from './components/Enquiry';

interface EnquiriesComponentProps {
  listing: Listing;
}

const EnquiriesComponent = (props: EnquiriesComponentProps) => {
  const enquiries = sortByDateAscending(props.listing.enquiries);

  useEffectOnce(() => {
    fireViewEnquiriesEvent(enquiries.length);
  });

  return (
    <Stack space="xlarge">
      <Heading level="2" component="h1">
        Your enquiries
      </Heading>

      <Stack space="medium">
        <Heading level="3" component="h2">
          {props.listing.title}
        </Heading>

        {enquiries.length > 0 && (
          <Text>
            {enquiries.length === 1
              ? '1 Enquiry Received'
              : `${enquiries.length} Enquiries Received`}
          </Text>
        )}

        {enquiries.length > 0 ? (
          <Stack space="xsmall">
            {enquiries.map((enquiry, index) => (
              <Enquiry key={index} enquiry={enquiry} />
            ))}
          </Stack>
        ) : (
          <NoEnquiriesMessage />
        )}
      </Stack>
    </Stack>
  );
};

const NoEnquiriesMessage = () => (
  <Card data={{ testid: 'no-enquiries-message' }}>
    <Stack space="medium">
      <Heading level="3">You haven&apos;t received any enquiries yet</Heading>

      <Text>
        You will be notified via email when someone enquires on your listing, or
        you can check back here anytime to see all your enquiries in one place.
      </Text>
    </Stack>
  </Card>
);

type Listing = {
  title: string;
  listingId: string;
  location: string;
  urlKey: string;
  enquiries: Array<{
    userId: string;
    enquiryId: string;
    listingId: string;
    name: string;
    emailAddress: string;
    message: string;
    phoneCountryCode: string | null;
    phoneNumber: string | null;
    lastUpdated: string;
  } | null> | null;
};

export const EnquiriesContainer = () => {
  const { id } = useParams();

  // This won't happen
  if (!id) {
    return (
      <PageContainer>
        <Error isFullPage={true} />
      </PageContainer>
    );
  }

  return <Enquiries listingId={id} />;
};

interface EnquiriesProps {
  listingId: string;
}

const Enquiries = (props: EnquiriesProps) => {
  const { loading, error, data } = useQuery(GetPrivateSellerEnquiriesDocument, {
    variables: { listingId: props.listingId },
  });

  if (loading) {
    return (
      <PageContainer>
        <CustomLoader />
      </PageContainer>
    );
  }

  const listing = data?.user?.listings?.[0];

  if (error || !listing) {
    return (
      <PageContainer>
        <Alert tone="critical" data={{ testid: 'enquiries-error' }}>
          <Text>
            There was an error loading your enquiries. If this issue persists
            please{' '}
            <TextLink
              href="https://www.seekbusiness.com.au/contact-us"
              hitArea="large"
            >
              contact us
            </TextLink>
          </Text>
        </Alert>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <EnquiriesComponent listing={listing} />
    </PageContainer>
  );
};

interface PageContainerProps {
  children: React.ReactNode;
}

const PageContainer = (props: PageContainerProps) => (
  <Page footer={<SiteWideFooter />} footerPosition="belowFold">
    <Header
      title="Seller enquiries"
      description="Seller enquiries"
      url="/sell/listings/:id/enquiries"
    />

    <Hidden below="tablet">
      <Divider />
    </Hidden>

    <Box paddingTop="xlarge">
      <PageBlock width="medium">{props.children}</PageBlock>
    </Box>
  </Page>
);

const sortByDateAscending = (enquiries: Listing['enquiries']) => {
  if (!enquiries) {
    return [];
  }

  const enquiriesCopy = [...enquiries.filter(isNonNullable)];
  enquiriesCopy.sort((a, b) => {
    const aDate = new Date(a.lastUpdated);
    const bDate = new Date(b.lastUpdated);
    if (aDate < bDate) {
      return 1;
    } else if (bDate < aDate) {
      return -1;
    }
    return 0;
  });
  return enquiriesCopy;
};

export default EnquiriesContainer;
